import { useCallback, useMemo, useState } from 'react';
import { User } from '../types/user';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isLoading: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  isLoading: true
};

// Helper function to safely access localStorage
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
      return null;
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('Error setting localStorage:', error);
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.warn('Error removing from localStorage:', error);
    }
  }
};

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    const storedAuth = safeLocalStorage.getItem('auth');
    if (storedAuth) {
      try {
        const { user, token } = JSON.parse(storedAuth) as { user?: User; token?: string };
        if (user && token) {
          return {
            isAuthenticated: true,
            user: {
              ...user,
              createdAt: user.createdAt ? new Date(user.createdAt) : (user as User).createdAt
            },
            token,
            isLoading: false
          };
        }
      } catch (error) {
        console.error('Error parsing stored auth:', error);
        safeLocalStorage.removeItem('auth');
      }
    }
    return { ...initialState, isLoading: false };
  });

  const login = useCallback((user: User, token: string) => {
    const newAuthState: AuthState = {
      isAuthenticated: true,
      user,
      token,
      isLoading: false
    };

    safeLocalStorage.setItem('auth', JSON.stringify(newAuthState));
    setAuthState(newAuthState);
  }, []);

  const logout = useCallback(() => {
    setAuthState(prev => ({ ...prev, isLoading: true }));
    safeLocalStorage.removeItem('auth');
    // Set flag to indicate logout happened - this will trigger hard refresh on login page
    try {
      sessionStorage.setItem('logoutPerformed', 'true');
    } catch (error) {
      console.warn('Error setting logout flag:', error);
    }
    setAuthState({ ...initialState, isLoading: false });
  }, []);

  const authContext = useMemo(() => ({
    ...authState,
    userRole: authState.user?.role ?? null,
    userId: authState.user?.id ?? null,
    userEmail: authState.user?.email ?? null,
    userName: authState.user?.name ?? null,
    userSurname: authState.user?.surname ?? null,
    login,
    logout
  }), [authState, login, logout]);

  return authContext;
}; 